import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { SummerNoteConfig } from 'src/app/common/summernote-config';
import { SopraConfigurationsService } from '../../services/sopra-configurations/sopra-configurations.service';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';


@Component({
  selector: 'app-sopra-service-details',
  templateUrl: './sopra-service-details.component.html',
  styleUrl: './sopra-service-details.component.scss'
})
export class SopraServiceDetailsComponent implements OnInit {

  _sopraServiceId!: number;
  pageTitle: string = '';
  submitted: boolean = false;
  spinner: boolean = false;
  showErrorMessage: boolean = false;
  errorMessage: string = '';
  summerNoteConfig = SummerNoteConfig.MAIN_CONFIG;
  private fileIdCounter = 0;
  saveDialogRef: MatDialogRef<AlertBoxComponent> = null as any;
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sopraConfigurationsService = inject(SopraConfigurationsService);

  sopraServiceFormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    description_for_expert: new FormControl('', Validators.required),
    is_default_in_sopra: new FormControl(false),
    is_active_for_sopra: new FormControl(false),
    terms_of_classification: new FormControl('', Validators.required),
    files: new FormArray([
      this.createNeededFileFormGroup(),
    ]),
  });

  createNeededFileFormGroup(fileData?: any): FormGroup {
    const hasExpiryDate = fileData?.has_expiry_date || false;
    const monthsToExpire = fileData?.months_to_expire || null;
    
    const monthsValidators = hasExpiryDate ? [Validators.required] : null;
    
    return new FormGroup({
      id: new FormControl(fileData?.id || null),
      _uniqueId: new FormControl(this.fileIdCounter++),
      name: new FormControl(fileData?.name || '', Validators.required),
      has_expiry_date: new FormControl(hasExpiryDate),
      months_to_expire: new FormControl(monthsToExpire, monthsValidators),
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id === 'add') {
        this.pageTitle = 'إضافة خدمة جديدة';
      } else {
        this.pageTitle = 'تعديل خدمة';
        this._sopraServiceId = +id;
        this.loadSopraServiceDetails();
      }
    });
  }

  loadSopraServiceDetails() {
    this.spinner = true;
    this.sopraConfigurationsService.getClassificationDetails(this._sopraServiceId).subscribe(details => {
      this.spinner = false;
      const classificationData = (details as any).data;

      this.patchsopraServiceDetails(classificationData);

      if (classificationData.files && Array.isArray(classificationData.files)) {
        this.setClassificationsFiles(classificationData.files);
      }
    }, error => {
      this.spinner = false;
    });
  }

  get files(): FormArray<FormGroup> {
    return this.sopraServiceFormGroup.get('files') as FormArray<FormGroup>;
  }

  patchsopraServiceDetails(details: any) {
    this.sopraServiceFormGroup.patchValue({
      name: details.name,
      description_for_expert: details.description_for_expert,
      is_default_in_sopra: details.is_default_in_sopra,
      is_active_for_sopra: details.is_active_for_sopra,
      terms_of_classification: details.terms_of_classification,
    });
  }

  setClassificationsFiles(files: any[]) {
    const filesFormArray = this.sopraServiceFormGroup.get('files') as FormArray;
    filesFormArray.clear();
    files.forEach((file: any) => {
      filesFormArray.push(this.createNeededFileFormGroup(file));
    });
  }

  addNeededFile() {
    this.files.push(this.createNeededFileFormGroup());
  }

  trackByFileId(index: number, item: FormGroup): any {
    // Use server ID if available (for saved files), otherwise use temporary unique ID (for new files)
    const serverId = item.get('id')?.value;
    const tempId = item.get('_uniqueId')?.value;
    return serverId || `temp_${tempId}`;
  }

  removeNeededFile(index: number): void {
    this.files.removeAt(index);
  }

  isValueChanged(event: boolean, controlName: string) {
    if (event) {
      this.sopraServiceFormGroup.get(controlName)?.setValue(true);
    } else {
      this.sopraServiceFormGroup.get(controlName)?.setValue(false);
    }
  }

  toggleHasExpiredDate(event: Event, index: number) {
    const neededFileGroup = this.files.at(index) as FormGroup;
    if ((event.target as HTMLInputElement).checked) {
      neededFileGroup.get('has_expiry_date')?.setValue(true);
      neededFileGroup.get('months_to_expire')?.setValidators(Validators.required);
      neededFileGroup.get('months_to_expire')?.updateValueAndValidity();
    } else {
      neededFileGroup.get('has_expiry_date')?.setValue(false);
      neededFileGroup.get('months_to_expire')?.setValidators(null);
      neededFileGroup.get('months_to_expire')?.updateValueAndValidity();
      neededFileGroup.get('months_to_expire')?.setValue(null);
    }
  }

  /**
   * Clean HTML content from Summernote editors
   * Removes empty HTML tags like <br>, <p></p>, etc.
   */
  private cleanHtmlContent(html: string | null | undefined): string {
    if (!html) return '';
    
    // Remove all HTML tags and check if there's any actual content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // If no text content exists, return empty string
    if (textContent.trim().length === 0) {
      return '';
    }
    
    return html;
  }

  closeError() {
    this.showErrorMessage = false;
    this.errorMessage = '';
  }

  save(event: Event) {
    this.submitted = true;
    this.showErrorMessage = false;
    
    // Clean HTML content from Summernote fields
    const descriptionValue = this.sopraServiceFormGroup.get('description_for_expert')?.value;
    const termsValue = this.sopraServiceFormGroup.get('terms_of_classification')?.value;
    
    this.sopraServiceFormGroup.patchValue({
      description_for_expert: this.cleanHtmlContent(descriptionValue),
      terms_of_classification: this.cleanHtmlContent(termsValue),
    });
    
    // Force form to update
    this.sopraServiceFormGroup.updateValueAndValidity();
    
    if (this.sopraServiceFormGroup.valid) {
      if (this._sopraServiceId) {
        this.updateClassification(event);
      } else {
        this.addClassification(event);
      }
    } else {
      this.errorMessage = 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح.';
      this.showErrorMessage = true;
    }
  }

  addClassification(event: any) {
    const formValue = this.sopraServiceFormGroup.getRawValue();
    const createData = {
      name: formValue.name,
      description_for_expert: formValue.description_for_expert,
      is_default_in_sopra: formValue.is_default_in_sopra,
      is_active_for_sopra: formValue.is_active_for_sopra,
      terms_of_classification: formValue.terms_of_classification,
      files: formValue.files?.map((file: any) => {
        const { _uniqueId, id, ...fileData } = file;
        // Include id only if it exists (for existing files)
        return id ? { id, ...fileData } : fileData;
      }),
    };
    this.sopraConfigurationsService.createClassification(createData, event.buttonId)
    .subscribe((response: any) => {
      this.showAlertBox('تم إنشاء الخدمة بنجاح', true);
      this.router.navigate([`pages/sopra/service/${response?.data?.id}`], {replaceUrl: true});
    }, error => {
      this.showAlertBox('حدث خطأ أثناء إنشاء الخدمة', false);
    });
  }

  updateClassification(event: any) {
    const formValue = this.sopraServiceFormGroup.getRawValue();
    const updateData = {
      name: formValue.name,
      description_for_expert: formValue.description_for_expert,
      is_default_in_sopra: formValue.is_default_in_sopra,
      is_active_for_sopra: formValue.is_active_for_sopra,
      terms_of_classification: formValue.terms_of_classification,
      files: formValue.files?.map((file: any) => {
        const { _uniqueId, id, ...fileData } = file;
        // Include id only if it exists (for existing files)
        return id ? { id, ...fileData } : fileData;
      }),
    };
    this.sopraConfigurationsService.updateClassification(this._sopraServiceId, updateData, event.buttonId)
    .subscribe(response => {
      this.showAlertBox('تم تحديث الخدمة بنجاح', true);
    }, error => {
      this.showAlertBox('حدث خطأ أثناء تحديث الخدمة', false);
    });
  }

  showAlertBox(message: string, isSuccess: boolean = true) {
    this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: isSuccess,
        message: message,
        button: 'تم',
      },
    });
  }
}
